import { useRef, useState, DragEvent } from 'react';
import { ArrowUpIcon, XIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

export const MAX_FILE_BYTES = 10 * 1024 * 1024;

interface FileDropProps {
  label: string;
  note?: string;
  required?: boolean;
  accept: string;
  hint: string;
  accentColor: string;
  multiple?: boolean;
  files: File[];
  onFiles: (files: File[]) => void;
}

/** Drag-and-drop upload area for one file (default) or several (`multiple`). */
function FileDrop({
  label,
  note,
  required,
  accept,
  hint,
  accentColor,
  multiple,
  files,
  onFiles,
}: FileDropProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [sizeError, setSizeError] = useState<string | null>(null);

  const addFiles = (incoming: FileList | null) => {
    if (!incoming || incoming.length === 0) return;
    const all = Array.from(incoming);
    const picked = all.filter((f) => f.size <= MAX_FILE_BYTES);
    const tooBig = all.filter((f) => f.size > MAX_FILE_BYTES).map((f) => f.name);
    setSizeError(tooBig.length ? `Over the 10MB limit and skipped: ${tooBig.join(', ')}` : null);
    if (picked.length === 0) return;
    onFiles(multiple ? [...files, ...picked] : [picked[0]]);
  };

  return (
    <div className="mb-4">
      <Label className="mb-1 text-sm font-semibold text-foreground">
        {label} {required && <span className="text-red-600">*</span>}
      </Label>
      {note && <p className="mb-2 text-xs text-muted-foreground">{note}</p>}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(e: DragEvent<HTMLDivElement>) => {
          e.preventDefault();
          setDragActive(false);
          addFiles(e.dataTransfer.files);
        }}
        className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-input bg-muted px-6 py-6 text-center transition-colors"
        style={dragActive ? { borderColor: accentColor } : undefined}
      >
        <Button
          onClick={() => inputRef.current?.click()}
          style={{ backgroundColor: accentColor }}
          className="text-white hover:opacity-90"
        >
          <ArrowUpIcon /> {multiple ? 'Upload file(s)' : 'Upload file'}
        </Button>
        <span className="text-xs text-muted-foreground">
          or drag and drop {multiple ? 'files' : 'a file'} here
        </span>
        <span className="text-xs text-muted-foreground">{hint}</span>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          hidden
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>
      {sizeError && <p className="mt-2 text-xs text-red-600">{sizeError}</p>}
      {files.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {files.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="flex items-center justify-between rounded-lg bg-green-50 px-4 py-2.5 text-sm text-green-700"
            >
              <span>📄 {file.name}</span>
              <button
                type="button"
                onClick={() => onFiles(files.filter((_, i) => i !== index))}
                className="text-green-700 hover:text-green-900"
                aria-label={`Remove ${file.name}`}
              >
                <XIcon className="size-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default FileDrop;
